import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Permission } from 'src/app/core/resources/permission';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { CargoAdvancePercentageComponent } from '../cargo-advance-percentage/cargo-advance-percentage.component';
import { CargoDetailService } from '../cargo-detail/cargo-detail.service';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { NgxSpinnerService } from 'ngx-spinner';
import { AccountService } from '../../account/account.service';
import { User } from 'src/app/core/interfaces/user';
import { VehiclesService } from '../../administration/vehicles/list-vehicles.service';
import { Vehicle } from 'src/app/core/interfaces/vehicle';
import { AuthService } from 'src/app/core/services/authentication.service';
import { Utils } from 'src/app/core/resources/utils';
import { AdminUsersService } from '../../administration/admin-users/admin-users.service';
import { ConsignmentCargo } from 'src/app/core/interfaces/consignmentCargo';
import { ServiceValidationManager } from 'src/app/core/managers/service-validation.manager';
import { FreightListService } from '../cargo-list/cargo-list.service';
import { Observable, Subscription } from 'rxjs';
import { GeneralConfigService } from 'src/app/core/services/general-config.service';
import { CargoPaymentsService } from 'src/app/core/services/cargo-payments.service';
import {
  DOCUMENT_RECREATION_IN_PROGRESS_MESSAGE,
  isDocumentRecreationInProgress
} from 'src/app/core/resources/transport-company-change.utils';
@Component({
  selector: 'app-generate-manifest-action',
  templateUrl: './generate-manifest-action.component.html',
  styleUrls: ['./generate-manifest-action.component.scss']
})
export class GenerateManifestActionComponent implements OnInit {
  // Inputs
  @Input() cargo: Cargo;
  @Input() type: 'cargo-option' | 'button-detail' | 'button-tracking';
  // Output
  @Output() refreshLoad = new EventEmitter();
  // Properties
  permission = Permission;
  readonly documentRecreationInProgressMessage = DOCUMENT_RECREATION_IN_PROGRESS_MESSAGE;
  userStateByValidationRules: { active: boolean, description: string } = { active: false, description: '' };
  rndcContingencyActive: boolean = false;
  rndcContingencySub: Subscription;
  constructor(
    public dialog: MatDialog,
    private cargoDetailService: CargoDetailService,
    private snackBarService: SnackBarService,
    private spinner: NgxSpinnerService,
    private accountService: AccountService,
    private vehiclesService: VehiclesService,
    private authService: AuthService,
    private utils: Utils,
    private adminUsersService: AdminUsersService,
    private serviceValidationManager: ServiceValidationManager,
    private cargoListService: FreightListService,
    private generalConfigService: GeneralConfigService,
    private cargoPaymentsService: CargoPaymentsService
  ) { }

  ngOnInit() {
    this.listenRndcContingencyState();
  }

  private listenRndcContingencyState(): void {
    this.rndcContingencySub = this.generalConfigService.rndcContingency$.subscribe((value: boolean) => this.rndcContingencyActive = value);
  }

  public canGenerateManifest(cargo: Cargo = this.cargo): boolean {
    return !!(cargo && cargo.state !== CargoStateEnum.CREATED && cargo.confirmedDriver
      && !isDocumentRecreationInProgress(cargo));
  }

  get documentRecreationInProgress(): boolean {
    return isDocumentRecreationInProgress(this.cargo);
  }

  private async openDialogNewAdvancePercentage(): Promise<{ proceed: boolean }> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      cargo: this.cargo,
      viewType: "viewAdvance"
    };
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.autoFocus = false;
    const result = await this.dialog.open(CargoAdvancePercentageComponent, dialogConfig).afterClosed().toPromise();
    if (result && result.state) return { proceed: true };
    return { proceed: false };
  }

  public async checkErrorToGenerateManifest() {
    if (isDocumentRecreationInProgress(this.cargo))
      return this.snackBarService.openSnackBar(
        this.documentRecreationInProgressMessage,
        undefined,
        'alert'
      );
    this.spinner.show();
    try {
      const cargosToValidate: Cargo[] = [];
      if (this.cargo.consecutiveMainCargo) {
        const subCargos = await this.getSubCargos(this.cargo.consecutiveMainCargo);
        cargosToValidate.push(...subCargos);
      } else
        cargosToValidate.push(this.cargo);

      for (const cargo of cargosToValidate) {
        if (isDocumentRecreationInProgress(cargo))
          return this.snackBarService.openSnackBar(
            `El servicio ${cargo.consecutive} tiene un proceso de recreación de remesas y manifiesto en curso.`,
            undefined,
            'alert'
          );
        if (!this.canGenerateManifest(cargo)) return this.snackBarService.openSnackBar(`El servicio ${cargo.consecutive} no cumple con los requisitos para generar el manifiesto.`, undefined, 'error');
        const cargoConsignments = await this.cargoDetailService.getConsignments(cargo.id, 'Created');
        if (!cargoConsignments || !Object.values(cargoConsignments).length) return this.cargoDetailService.dialogConsignmentAlerts({ "No hay remesas asociadas al servicio": `Se requiere asociar al menos una remesa en el servicio ${cargo.consecutive} para generar el manifiesto.` });
        const { canContinue } = await this.checkCargoValidToContinue(cargo, cargoConsignments ? Object.values(cargoConsignments) : []);
        if (!canContinue) return;
        const { cargoConsignmentsAlerts } = this.cargoDetailService.updateConsignmentFieldsAndAlerts(cargo, cargoConsignments);
        if (cargoConsignmentsAlerts && Object.keys(cargoConsignmentsAlerts).length > 0 &&
          Object.keys(cargoConsignmentsAlerts).some(consignmentKey => cargoConsignmentsAlerts[consignmentKey] !== null))
          this.cargoDetailService.dialogConsignmentAlerts(cargoConsignmentsAlerts, 'manual', cargo.consecutive);
        const manifestTypeCode = this.utils.getNestedValue(cargo, 'manifestType.code');
        // Validación para manifestos multiparada
        if (manifestTypeCode === 'M' && !(Object.values(cargoConsignments).length >= 2 || this.utils.isDefined(cargo.consecutiveMainCargo)))
          return this.snackBarService.openSnackBar('Debe diligenciar al menos dos remesas para manifestos multiparada', undefined, 'error');
        let areValid: boolean = await this.validationsToGenerateManifest(cargo);
        if (!areValid) return;
      }
      this.spinner.hide();
      const { proceed } = await this.openDialogNewAdvancePercentage();
      if (!proceed) return;
      this.generateManifest();
    } catch (error) {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
    } finally {
      this.spinner.hide();
    }
  }

  private async getSubCargos(mainConsecutive: number): Promise<Cargo[]> {
    try {
      const response = await this.cargoListService.getListCargo(`consecutiveMainCargo=${mainConsecutive}`).toPromise();
      const subCargos = response.results;
      return subCargos && subCargos.length ? subCargos : [];
    } catch (error) {
      return [];
    }
  }

  private async validationsToGenerateManifest(cargo: Cargo) {
    if (!cargo) {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_RETRY_INDICATION, undefined, 'error');
      return false;
    }
    // Second driver validation
    if (cargo.secondDriver) {
      let isSecondDriverActive = await this.isSecondDriverActive(cargo);
      if (!isSecondDriverActive) {
        this.snackBarService.openSnackBar(ServiceMessages.DRIVER_NOT_ACTIVE, undefined, 'error');
        return false;
      }
    }
    // Rate and cost 0 validation
    if (!this.cargoPaymentsService.isShippingCostZero(cargo)) return true;
    if (!this.isValidSaasDocument()) {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_RETRY_INDICATION, undefined, 'error');
      return false;
    }
    const vehicle: Vehicle = await this.getVehicle();
    if (!this.isValidVehicle(vehicle)) {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_RETRY_INDICATION, undefined, 'error');
      return false;
    }
    if (this.utils.vehicleIsOwnFleet(vehicle, this.authService.getCompanySaaSConfig())) return true;
    this.snackBarService.openSnackBar(ServiceMessages.SAME_OWNER_VEHICLE_LOAD_TO_RATE_COST_ZERO, undefined, 'error');
    return false;
  }

  private isValidSaasDocument(): boolean {
    return !!(this.authService && this.authService.getCompanySaaS() && this.authService.getCompanySaaS().companyId);
  }

  private isValidVehicle(vehicle: Vehicle): boolean {
    return !!vehicle && ((!!vehicle.owner && !!vehicle.owner.document) || (!!vehicle.administrator && !!vehicle.administrator.document));
  }

  private generateManifest(): void {
    if (isDocumentRecreationInProgress(this.cargo)) {
      this.snackBarService.openSnackBar(
        this.documentRecreationInProgressMessage,
        undefined,
        'alert'
      );
      return;
    }
    this.spinner.show();
    this.cargoDetailService.generateManifest(this.cargo.id).subscribe({
      next: async (cargo: Cargo) => {
        if (cargo) this.cargo = { ...this.cargo, ...cargo };
        this.spinner.hide();
        if (this.type === 'button-detail' || this.type === 'cargo-option') {
          this.refreshLoad.emit(cargo.consecutive.toString());
        }
        if (cargo && cargo.manifestError && cargo.manifestError.error && !this.rndcContingencyActive) {
          this.snackBarService.openSnackBar(cargo.manifestError.error, undefined, 'error');
        } else if (cargo && cargo.manifest) {
          this.snackBarService.openSnackBar(ServiceMessages.CARGO_GENERATE_MANIFEST_SUCCESS, undefined, 'success');
        } else {
          this.snackBarService.openSnackBar(ServiceMessages.GENERAL_RETRY_INDICATION, undefined, 'error');
        }
      },
      error: (error: unknown) => {
        this.spinner.hide();
        this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
      },
    });
  }

  async isSecondDriverActive(cargo: Cargo) {
    try {
      this.spinner.show();
      const driver: User = await this.accountService.validateEntity(1, cargo.secondDriver).toPromise();
      if (driver && driver.state && this.utils.isDefined(driver.state.active)) {
        return await this.checkStateDriver(driver);
      }
      return false;
    } catch (error) {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
      return false;
    } finally {
      this.spinner.hide();
    }
  }

  async checkStateDriver(driver: User) {
    const { active } = await this.adminUsersService.getUserStateByValidationRules(driver, 'Driver');
    return active;
  }

  public async getVehicle() {
    try {
      this.spinner.show();
      const vehicle: Vehicle = await this.vehiclesService.getVehicleFromOwnCompany(this.cargo.licensePlate).toPromise();
      return vehicle;
    } catch (error) {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  private async checkCargoValidToContinue(cargo: Cargo, consignments: ConsignmentCargo[]): Promise<{ canContinue: boolean }> {
    const vehicle = cargo.licensePlate ? await this.getVehicle() : null;
    const isValid = await this.serviceValidationManager.validateAllSectionsAndShowModal(cargo, vehicle, consignments, true);
    return { canContinue: isValid };
  }

  ngOnDestroy(): void {
    if (this.rndcContingencySub) this.rndcContingencySub.unsubscribe();
  }

  // GETTERS
  get cargoStateEnum(): typeof CargoStateEnum {
    return CargoStateEnum;
  }

}
